import {getUsername} from '../../../_shared/GWTBridge/invoke/genPage';
import {
    fetchDataByQueryParams,
    startStandAloneForm,
    startSubformWithoutFetch,
    updateCurrentRecordField
} from '../../Form/actions/standAloneForm';
import {APIActionStatus, Column, Form, Record, Screen, formsStore} from '../../../stores/Form';
import {Sort} from '../../../stores/Form/Sort';
import {formatDate} from '../../Form/utils/date';
import {closeFormBySession} from '../../Form/actions/form';
import {stageStepsMapping} from '../constants';
import {EditField, InputFieldsReturn, ProcData} from 'priority-web-sdk';
import {
    clearEOYRemindersStoreData,
    saveCompletedStepUser,
    saveEoyStepAssignedBy
} from './reminders';
import {fieldValue} from '../../../stores/Form/Field';
import {processProcStepResult} from '../../../services/webSdk/proc';
import {startProcedure} from '../../../services/webSdk/service';

export const getEoyLog = async (): Promise<Form> => {
    const eoyLogForm: Form = await startStandAloneForm('EOYLOG', ['NSCUST', 'EXTTYPE'], true);
    if (eoyLogForm) {
        const yearColumn: Column = eoyLogForm.getColumnByName('GLNAME');
        await fetchDataByQueryParams(eoyLogForm, {GLNAME: '*'}, [
            new Sort({ColumnID: yearColumn.id, Desc: true})
        ]);
        const newScreen = new Screen(eoyLogForm, null, true);
        formsStore.addForm(eoyLogForm.session, newScreen);
        return eoyLogForm;
    }
    return null;
};

export const checkEoyRecord = (eoyLogForm: Form): boolean => {
    const record: Record = eoyLogForm.records[0];
    return record && !record.isEmpty();
};

export const isWizardFinished = (eoyLogForm: Form): boolean => {
    const processEndDateColumn: Column = eoyLogForm.getColumnByName('ENDDATE');
    const processEndDate: fieldValue = eoyLogForm.currentRecord.getField(
        processEndDateColumn.id
    )?.origValue;
    return !!processEndDate;
};

export const updateStepStatus = async (
    stepId: string,
    ownerName: string
): Promise<APIActionStatus> => {
    if (!stepId) {
        return APIActionStatus.passed;
    }
    const eoyLogForm: Form = await getEoyLog();
    if (!eoyLogForm || isWizardFinished(eoyLogForm)) {
        return APIActionStatus.blocked;
    }
    const subForm = await fetchStepsFormByStepId(eoyLogForm, stepId);
    if (subForm) {
        let isSuccess = await updateCurrentRecordField(subForm, 'COMPLETED', true);
        if (isSuccess) {
            const curDateTime = formatDate(new Date(), subForm.getColumnByName('UDATE').Type);
            isSuccess = await updateCurrentRecordField(subForm, 'UDATE', curDateTime);
            if (isSuccess) {
                isSuccess = await updateCurrentRecordField(subForm, 'USERLOGIN', getUsername());
                if (isSuccess) {
                    isSuccess = await formsStore
                        .getScreenBySession(eoyLogForm.session)
                        .saveRecord();
                    if (isSuccess) {
                        const finalStage = stageStepsMapping[stageStepsMapping.length - 1];
                        const finalStepID = finalStage.steps[finalStage.steps.length - 1]?.logId;

                        if (stepId === finalStepID) {
                            await formsStore
                                .getScreenBySession(eoyLogForm.session)
                                .handleFormLevel(0);
                            const curDateTime = formatDate(
                                new Date(),
                                eoyLogForm.getColumnByName('ENDDATE').Type
                            );
                            isSuccess = await updateCurrentRecordField(
                                eoyLogForm,
                                'ENDDATE',
                                curDateTime
                            );

                            /* If the final EOY Wizard step reset reminders store data */
                            clearEOYRemindersStoreData();
                        } else {
                            await saveCompletedStepUser(stepId, ownerName);
                        }

                        if (isSuccess) {
                            await closeFormBySession(eoyLogForm.session, false);
                            return APIActionStatus.passed;
                        }
                    }
                }
            }
        }
    }
    await closeFormBySession(eoyLogForm.session, false);
    return APIActionStatus.blocked;
};

export const updateAssignee = async (
    session: string,
    assignee: string
): Promise<APIActionStatus> => {
    const subForm = formsStore.getScreenBySession(session).getActiveForm();
    if (subForm) {
        const isSuccess = await updateCurrentRecordField(subForm, 'USERLOGIN', assignee);
        if (isSuccess) {
            await saveEoyStepAssignedBy(getUsername());
            await closeFormBySession(subForm.session, false);
            return APIActionStatus.passed;
        }
    }
    return APIActionStatus.blocked;
};

export const getLastUnfinishedStep = async (eoyLogForm: Form): Promise<string | null> => {
    await startSubformWithoutFetch(eoyLogForm, 'EOYSTEPS', true);
    const subForm = eoyLogForm.activeSubForm;
    if (subForm) {
        formsStore.getScreenBySession(eoyLogForm.session).setActiveLevel(1);
        await fetchDataByQueryParams(subForm, {COMPLETED: ''});

        subForm.setCurrentRow(0);
        if (!subForm.currentRecord.isNew) {
            return subForm.currentRecord
                .getField(subForm.getColumnByName('EOYSTEPNUM').id)
                .origValue.toString();
        }
    }
    return null;
};

export const fetchStepsFormByStepId = async (eoyLogForm: Form, stepId: string): Promise<Form> => {
    await startSubformWithoutFetch(eoyLogForm, 'EOYSTEPS');
    const subForm = eoyLogForm.activeSubForm;
    if (subForm) {
        formsStore.getScreenBySession(eoyLogForm.session).setActiveLevel(1);
        await fetchDataByQueryParams(subForm, {EOYSTEPNUM: stepId});
        subForm.setCurrentRow(0);
        return subForm;
    }
    return null;
};

export const apploadAttachmentsToLog = async (
    url: string,
    exttype: string,
    nscust: string,
    msg: string
): Promise<any> => {
    const procRes: ProcData = await startProcedure('EOYATTACHMENT', 'P');
    return processProcStepResult(
        procRes,
        updateAttachInputFields(url, exttype, nscust, msg),
        undefined
    );
};

const updateAttachInputFields =
    (url: string, exttype: string, nscust: string, msg: string) =>
    (inputFields: EditField[]): InputFieldsReturn => {
        const inputFieldsReturn = inputFields.map(field => {
            const urlFieldLength = 100;
            let value = field.value;

            switch (field.field) {
                case 1:
                    value = url.slice(-urlFieldLength);
                    break;
                case 2:
                    value = exttype;
                    break;
                case 3:
                    value = nscust;
                    break;
                case 4:
                    value = 'EOYLOG';
                    break;
                case 5:
                    value = msg;
                    break;
                default:
                    break;
            }

            return {
                field: field.field,
                op: 0,
                value: value,
                op2: 0,
                value2: value
            };
        });
        return {EditFields: inputFieldsReturn};
    };
